"use client";

import { CSSProperties, FC, useEffect, useMemo, useRef, useState } from "react";
import * as z from "@/lib/propSchema";
import Image from "next/image";
import { cn } from "@/lib/utils";
import { Button } from "@/components/atoms/Button";
import { useDispatch } from "react-redux";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import { LEAD_DROP_VARIANTS } from "../../../constants";
// [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → central widget_impression; see webapp/docs/analytics
import { leadCtaClick } from "@/customAnalytics/trackingEvents";
import { formatBackgroundStyle } from "@/utils/styles/format";
import { useIsMobile } from "@/hooks/useIsMobile";
import RevealOnScroll from "@/components/atoms/RevealOnScroll";
import TiltCard from "@/components/atoms/TiltCard";

/** Section copy cap — aligned with `PROGRAM_BENEFITS2_COPY_LIMITS.subText` (250). */
const PROGRAM_BENEFITS1_COPY_LIMITS = {
  subText: 250,
} as const;

function clampStr(s: string, max: number) {
  return s.length <= max ? s : s.slice(0, max);
}

const MetadataSchema = z.object({
  id: z.union([z.number(), z.string()]),
  meta_image: z.string(),
  meta_title: z.string(),
  meta_sub_title: z.string(),
  meta_alt_image: z.string().optional(),
  meta_title_color: z.string().optional().default(""),
  meta_sub_title_color: z.string().optional().default(""),
  meta_video: z.string().optional(),
  meta_priority: z.coerce.number().optional().default(0),
  meta_card_blur_effect: z.boolean().optional().default(false),
});

type MetadataType = z.infer<typeof MetadataSchema>;

const ProgramBenefits1DataSchema = z.object({
  pre_title: z.string().optional().default("Why Choose"),
  pre_title_color: z.string().optional().default(""),
  title: z.string().optional().default("This Program"),
  title_start_color: z.string().optional().default(""),
  title_end_color: z.string().optional().default(""),
  title_gradient_direction: z.string().optional().default(""),
  description: z
    .string()
    .optional()
    .nullable()
    .default("")
    .transform((s) => clampStr(String(s ?? "").trim(), PROGRAM_BENEFITS1_COPY_LIMITS.subText)),
  description_color: z.string().nullable().optional().default("#FFFFFFAF"),
  desktop_background_color: z.string().optional().default(""),
  mobile_background_color: z.string().optional().default(""),
  backvariant: z.enum(["variant_1", "variant_2", "variant_3"]).optional().default("variant_3"),
  font_style: z.string().optional().default("var(--font-google-sans)"),
  gradient_color_1: z.string().optional().default(""),
  gradient_color_2: z.string().optional().default(""),
  gradient_color_3: z.string().optional().default(""),
  gradient_color_4: z.string().optional().default(""),
  metadata: z.array(MetadataSchema).optional().default([]),
  showLoadMore: z.boolean().optional().default(true),
  loadMoreText: z.string().optional().default("Load More"),
  loadMoreTextColor: z.string().optional().default(""),
  load_more_button: z.string().nullable().optional().default("Load More"),
  load_more_button_text_color: z.string().nullable().optional(),
  /** MySQL / API may send 0/1; match ProgramBenefits2 / ParaText truthy handling. */
  enable_animation: z
    .preprocess(
      (v) => v === true || v === 1 || v === "1",
      z.boolean(),
    )
    .optional()
    .default(false),
  button_text: z.string().optional().default("Apply Now"),
  button_text_color: z.string().optional().default(""),
  button_color: z.string().optional().default(""),
  initialCardsToShow: z.number().optional().default(3),
}).transform(data => {
  // sort metadata by priority
  return {
    ...data,
    metadata: data.metadata?.sort((a: MetadataType, b: MetadataType) => (a.meta_priority || 0) - (b.meta_priority || 0)),
  };
});

type ProgramBenefits1Data = z.infer<typeof ProgramBenefits1DataSchema>;
type Metadata = z.infer<typeof MetadataSchema>;

// Play icon SVG component
const PlayIcon: FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="84" height="84" viewBox="0 0 84 84" fill="none" className={className}>
    <path d="M41.6667 0C18.6667 0 0 18.6667 0 41.6667C0 64.6667 18.6667 83.3333 41.6667 83.3333C64.6667 83.3333 83.3333 64.6667 83.3333 41.6667C83.3333 18.6667 64.7083 0 41.6667 0ZM54.1667 50.9583L42.0833 57.9167C40.5833 58.7917 38.9167 59.2083 37.2917 59.2083C35.625 59.2083 34 58.7917 32.5 57.9167C29.5 56.1667 27.7083 53.0833 27.7083 49.5833V35.625C27.7083 32.1667 29.5 29.0417 32.5 27.2917C35.5 25.5417 39.0833 25.5417 42.125 27.2917L54.2083 34.25C57.2083 36 59 39.0833 59 42.5833C59 46.0833 57.2083 49.2083 54.1667 50.9583Z" fill="white"/>
  </svg>
);

// VideoCard Component
const VideoCard: FC<{ card: Metadata; fontStyle: string }> = ({ card, fontStyle }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isTouchDevice, setIsTouchDevice] = useState(false);


  // Detect touch device
  useEffect(() => {
    setIsTouchDevice('ontouchstart' in globalThis || navigator.maxTouchPoints > 0);
  }, []);

  // Handle video play/pause based on hover (desktop) or manual toggle (mobile)
  useEffect(() => {
    if (!videoRef.current || !card.meta_video) return;

    if (isPlaying) {
      videoRef.current.play().catch(() => {
        // Handle autoplay restrictions
        setIsPlaying(false);
      });
    } else {
      videoRef.current.pause();
      videoRef.current.currentTime = 0; // Reset to start
    }
  }, [isPlaying, card.meta_video]);

  // Desktop hover behavior
  const handleMouseEnter = () => {
    if (card.meta_video && !isTouchDevice) {
      setIsPlaying(true);
    }
  };

  const handleMouseLeave = () => {
    if (!isTouchDevice) {
      setIsPlaying(false);
    }
  };

  // Mobile tap behavior - toggle play/pause
  const handleClick = () => {
    if (card.meta_video && isTouchDevice) {
      setIsPlaying(prev => !prev);
    }
  };

  // Keyboard support for accessibility
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (card.meta_video && isTouchDevice && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      setIsPlaying(prev => !prev);
    }
  };

  return (
    <div
      role="button"
      tabIndex={card.meta_video && isTouchDevice ? 0 : -1}
      aria-label={card.meta_video ? (isPlaying ? `Pause ${card.meta_title} ${card.meta_sub_title}` : `Play ${card.meta_title} ${card.meta_sub_title}`) : card.meta_title}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={cn(
        "relative overflow-clip shrink-0 group cursor-default",
        // Mobile: full width, 430px height, 28px radius
        "xs:w-[335px] xs:h-[430px] rounded-[28px]",
        // Small Desktop (1080px): 306px width, 420px height, 30px radius
        "sm:w-[306px] sm:h-[420px] sm:mx-auto sm:rounded-[30px]",
        "md:w-[340px] md:h-[468px] md:rounded-[33.438px]",
        // Medium Desktop (1230px): 340px width, 468px height, 33.438px radius
        "lg:w-[408px] lg:h-[560px] lg:rounded-[40px]",
        // Large Desktop (1430px): 408px width, 560px height, 40px radius
        "xl:w-[460px] xl:h-[640px] xl:rounded-[45.813px]"
      )}
    >
      {/* Background Image */}
      <div
        className={cn(
          "absolute inset-0 overflow-hidden pointer-events-none transition-opacity duration-300",
          "rounded-[28px]",
          "sm:rounded-[30px]",
          "lg:rounded-[33.438px]",
          "xl:rounded-[40px]",
          isPlaying ? "opacity-0" : "opacity-100"
        )}
      >
        <Image
          src={card.meta_image}
          alt={card?.meta_alt_image || card.meta_title}
          fill
          className="object-cover"
        />
      </div>

      {/* Video Element */}
      {card.meta_video && (
        <video
          ref={videoRef}
          className={cn(
            "absolute inset-0 w-full h-full object-cover transition-opacity duration-300",
            "rounded-[28px]",
            "sm:rounded-[30px]",
            "lg:rounded-[33.438px]",
            "xl:rounded-[40px]",
            isPlaying ? "opacity-100" : "opacity-0"
          )}
          loop
          muted
          playsInline
        >
          <source src={card.meta_video} type="video/mp4" />
        </video>
      )}

      {
        card.meta_card_blur_effect && (
          <div
            className={cn(
              "absolute inset-x-0 top-0 pointer-events-none transition-opacity duration-300",
              "h-[202px] sm:h-[175px] md:h-[190px] lg:h-[210px] xl:h-[270px]",
              "backdrop-blur-[20px]",
              "backdrop-saturate-[110%]",
              "backdrop-brightness-110",
              "[mask-image:linear-gradient(to_bottom,black_0%,black_65%,rgba(0,0,0,0.2)_90%,transparent_97%)]",
              // "bg-[linear-gradient(180deg, rgba(0, 0, 0, 0.80) 6.91%, rgba(0, 0, 0, 0.00) 99.79%)]"
            )}
          />
        )
      }

      {/* Dark overlay for video state */}
      {card.meta_video && !isPlaying && (
        <div
          className={cn(
            "absolute inset-0 bg-black/20 pointer-events-none",
            "rounded-[28px]",
            "sm:rounded-[30px]",
            "lg:rounded-[33.438px]",
            "xl:rounded-[40px]"
          )}
        />
      )}

      {/* Title */}
      <p
        className={cn(
          "absolute font-semibold leading-[1.4] text-white transition-opacity duration-300 line-clamp-2",
          // Mobile: 18px, left-20px, top-20px, w-289px, tracking-0.18px
          "xs:text-[18px] xs:left-[20px] xs:top-[20px] xs:w-[289px] xs:tracking-[0.18px]",
          // Tablet: 18px, left-20px, top-20px, w-266px, tracking-0.18px
          "sm:text-[18px] sm:left-[20px] sm:top-[20px] sm:xw-[266px] sm:tracking-[0.18px]",
          "md:text-[20px] md:left-[20px] md:top-[20px] md:xw-[300.102px] md:tracking-[0.18px]",
          // Desktop: 20px, left-20.06px, top-20.06px, w-300.102px, tracking-0.2px
          "lg:text-[24px] lg:left-[20.06px] lg:top-[20.06px] lg:w-[359px] lg:tracking-[0.2px]",
          // Large Desktop: 24px, left-24px, top-24px, w-359px, tracking-0.24px
          "xl:text-[28px] xl:left-[24px] xl:top-[24px] xl:w-[411.167px] xl:tracking-[0.24px]",
          isPlaying ? "opacity-0" : "opacity-100"
        )}
        style={{
          fontFamily: "var(--font-google-sans)",
          color: card.meta_title_color
        }}
      >
        {card.meta_title}
      </p>

      {/* Description */}
      <p
        className={cn(
          "absolute font-medium leading-[1.49] text-white transition-opacity duration-300 line-clamp-3",
          // Mobile: 14px, left-20px, top-80px, w-295px (20px right padding)
          "xs:text-[14px] xs:left-[20px] xs:top-[80px] xs:w-[295px]",
          // Tablet: 12px, left-20px, top-78px, w-266px
          "sm:text-[12px] sm:left-[20px] sm:top-[78px] sm:w-[266px]",
          "md:text-[12px] md:left-[20px] md:top-[78px] md:w-[300.102px]",
          // Desktop: 12px, left-20.06px, top-84.43px, w-300.102px
          "lg:text-[14px] lg:left-[20.06px] lg:top-[84.43px] lg:w-[359px]",
          // Large Desktop: 14px, left-24px, top-101px, w-359px
          "xl:text-[16px] xl:left-[24px] xl:top-[101px] xl:w-[411.167px]",
          isPlaying ? "opacity-0" : "opacity-100"
        )}
        style={{
          fontFamily: "var(--font-google-sans)",
          color: card.meta_sub_title_color
        }}
      >
        {card.meta_sub_title}
      </p>

      {/* Play Button - Show only if video exists and not playing */}
      {card.meta_video && !isPlaying && (
        <div className="absolute top-[55%] left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
          <PlayIcon
            className={cn(
              "w-[55px] h-[55px]",
              // "sm:w-[70px] sm:h-[70px]",
              // "lg:w-[80px] lg:h-[80px]",
              "drop-shadow-[0px_4px_4px_rgba(0,0,0,0.25)]"
            )}
          />
        </div>
      )}
    </div>
  );
};

export const ProgramBenefits1: FC<any> = (props) => {
  // Props can be structured as either { data } or { data: { data } }
  // Need to extract the actual component data
  const actualData = props?.data?.data || props?.data || props;
  // const pageCategory = props?.pageCategory || props?.data?.pageCategory;

  const dispatch = useDispatch();
  const sectionRef = useRef<HTMLElement>(null);
  const [showAllCards, setShowAllCards] = useState(false);

  const isMobile = useIsMobile();

  const parseResult = useMemo(() => ProgramBenefits1DataSchema.safeParse(actualData), [actualData]);

  // Track widget viewed
  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (sectionRef.current) {
  //     FireWidgetViewedEvent(sectionRef, "why_choose_this_program");
  //   }
  // }, []);

  if (!parseResult.success) {
    console.error("❌ Zod validation failed:", parseResult.error);
    return null;
  }

  const parsedData: ProgramBenefits1Data = parseResult.data;
  const isAnimationEnabled = parsedData.enable_animation;

  // Filter and sort active metadata items (min 3, max 6)
  const activeCards = parsedData.metadata.slice(0, 6) // Max 6 cards

  const initialCardsToShow = parsedData.initialCardsToShow;
  // Mobile (sm:hidden): show initialCardsToShow until "Load More" clicked. No isMobile — avoids delay.
  const mobileVisibleCards =
    parsedData.showLoadMore && !showAllCards
      ? activeCards.slice(0, initialCardsToShow)
      : activeCards;

  const shouldShowLoadMore =
    parsedData.showLoadMore &&
    !showAllCards &&
    activeCards.length > initialCardsToShow;

  const handleLoadMore = () => {
    setShowAllCards(true);
  };

  const handleCtaClick = () => {
    leadCtaClick({
      section: "why_choose_this_program",
      component: "cta_button",
      label: parsedData.button_text,
    });
    dispatch(
      handleOpenLeaddrop({
        variant: LEAD_DROP_VARIANTS.REGISTER,
      })
    );
  };

  const bgColor = formatBackgroundStyle(parsedData, isMobile || false, "#000000");

  return (
    <section
      ref={sectionRef}
      className={cn(
        "relative w-full overflow-hidden flex justify-center",
        "xs:px-[20px] xs:py-[40px]",
        "sm:px-[60px] sm:py-[40px]",
        "md:px-[80px] md:py-[60px]",
        "lg:px-[80px] lg:py-[60px]",
        "xl:px-[140px] xl:py-[80px]",
      )}
      style={bgColor}
    >
      <div
        className={cn(
          "relative z-10",
          "w-[335px]",
          "sm:w-[960px]",
          "md:w-[1070px]",
          "lg:w-[1280px]",
          "xl:w-[1470px]",
        )}
      >
        {/* Title Section */}
        <div
          className={cn(
            "relative flex flex-col mb-[24px]",
            "sm:mb-[27px]",
            "lg:mb-[36px]",
          )}
        >
          {/* Both lines are one <h2>. Spans throughout, because a heading may only hold
              phrasing content; `block` restores the line break the <p> gave. */}
          <h2 className="flex flex-col">
          <RevealOnScroll as="span" enableAnimation={isAnimationEnabled}>
            <span
              className={cn(
                "block font-semibold leading-normal whitespace-nowrap white-title-wf",
              )}
              style={{
                color: parsedData.pre_title_color,
                fontFamily: "var(--font-google-sans)",
              }}
            >
              {parsedData.pre_title}
            </span>
          </RevealOnScroll>

          <RevealOnScroll as="span" enableAnimation={isAnimationEnabled}>
            <span
              className={cn(
                "font-bold leading-[1.2] line-clamp-2 sm:whitespace-nowrap bg-clip-text gradient-title-denton mb-6",
              )}
              style={{
                backgroundImage:
                  parsedData.title_gradient_direction &&
                  parsedData.title_start_color &&
                  parsedData.title_end_color
                    ? `linear-gradient(${parsedData.title_gradient_direction}, ${parsedData.title_start_color}, ${parsedData.title_end_color})`
                    : undefined,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                fontFamily: parsedData.font_style,
              }}
            >
              {parsedData.title}
            </span>
          </RevealOnScroll>
          </h2>
          {parsedData.description ? (
            <RevealOnScroll enableAnimation={isAnimationEnabled}>
              <p
                className={cn(
                  "w-full min-w-0 wrap-break-word",
                  "font-semibold leading-[1.49] mb-[40px]",
                  "text-[12px]",
                  "sm:text-[14px]",
                  "md:text-[16px]",
                  "lg:text-[18px] xl:text-[18px]",
                )}
                style={{
                  color:
                    parsedData.description_color || "rgba(255,255,255,0.7)",
                  fontFamily: "var(--font-wf-visual-sans)",
                }}
              >
                {parsedData.description}
              </p>
            </RevealOnScroll>
          ) : null}
        </div>

        {/* Mobile: single column + Load More (sm:hidden — visible immediately, no useIsMobile delay) */}
        <div
          className={cn(
            "relative flex flex-col justify-center sm:hidden",
            "gap-[24px] w-[354px]",
          )}
        >
          {mobileVisibleCards.map((card) => (
            <RevealOnScroll key={card.id} enableAnimation={isAnimationEnabled}>
              <VideoCard
                key={card.id}
                card={card}
                fontStyle={parsedData.font_style}
              />
            </RevealOnScroll>
          ))}
          {shouldShowLoadMore && (
            <RevealOnScroll enableAnimation={isAnimationEnabled}>
              <div className="relative flex justify-center">
                <button
                  onClick={handleLoadMore}
                  type="button"
                  data-cta-name="load_more"
                  className={cn(
                    "font-semibold leading-[1.4] tracking-[0.18px] underline text-center cursor-pointer",
                    "text-[18px]",
                  )}
                  style={{
                    color:
                      parsedData.load_more_button_text_color ||
                      parsedData.loadMoreTextColor,
                  }}
                >
                  {parsedData.load_more_button ?? parsedData.loadMoreText}
                </button>
              </div>
            </RevealOnScroll>
          )}
        </div>

        {/* Desktop: grid, all cards (hidden sm:flex) */}
        <div
          className={cn(
            "relative flex flex-wrap justify-center hidden sm:flex",
            "sm:gap-[20px] sm:w-[960px]",
            "md:gap-[24px] md:w-[1070px]",
            "lg:gap-[28px] lg:w-[1280px]",
            "xl:gap-[40px] xl:w-[1470px]",
          )}
        >
          {activeCards.map((card, index) => {
            const isFirstCard = index === 0;
            const isLastCard = index === activeCards.length - 1;
            const neitherFirstNorLastCard = !isFirstCard && !isLastCard;
            return (
              <RevealOnScroll
                key={card.id}
                enableAnimation={isAnimationEnabled}
                scaleStart={0.8}
                y={neitherFirstNorLastCard ? 100 : 0}
                x={isFirstCard ? -200 : isLastCard ? 200 : 0}
              >
                <TiltCard enableTilt={isAnimationEnabled}>
                  <VideoCard
                    key={card.id}
                    card={card}
                    fontStyle={parsedData.font_style}
                  />
                </TiltCard>
              </RevealOnScroll>
            );
          })}
        </div>

        {/* CTA Button */}
        {parsedData.button_text && (
          <div className="relative flex justify-center ">
            <RevealOnScroll>
              <Button
                onClick={handleCtaClick}
                className={cn(
                  "w-full font-semibold leading-normal tracking-[0.16px] xs:mt-[24px] sm:mt-[40px] md:mt-[36px] btn-base btn-white",
                  "text-[16px] px-[24px] py-[16px]",
                  "sm:w-auto sm:px-[20px] sm:py-[14px]",
                  "lg:px-[24px] lg:py-[16px]",
                )}
                style={{
                  backgroundColor: parsedData.button_color,
                  color: parsedData.button_text_color,
                }}
              >
                {parsedData.button_text}
              </Button>
            </RevealOnScroll>
          </div>
        )}
      </div>
    </section>
  );
};

// export default WhyChooseThisProgram;
